import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  BookOpen, Plus, Sparkles, Download, Trash2, Loader2, ChevronLeft, ChevronRight, FileText, FileDown, Save, CheckCircle2,
} from 'lucide-react';

/*
 * v1.1.8 — Manuale HACCP (Crea Manuale)
 * Lista manuali + wizard creazione + editor sezioni con AI assistance.
 */

const STATUS_BADGE = {
  draft: { label: 'Bozza', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  finalized: { label: 'Finalizzato', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
};

export default function HaccpManualePage() {
  const [view, setView] = useState('list'); // 'list' | 'editor'
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ tipologie: {}, sezioni_standard: [] });
  const [aziende, setAziende] = useState([]);
  const [pdvList, setPdvList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(null); // manuale in editing
  const [activeSection, setActiveSection] = useState(0);

  const [openCreate, setOpenCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    titolo: '', tipologia: 'ristorante', azienda_id: '', ul_id: '', use_ai: true,
  });

  const [regenerating, setRegenerating] = useState(null); // section_id

  const load = async () => {
    setLoading(true);
    try {
      const [mRes, mtRes, aRes, pRes] = await Promise.all([
        api.get('/haccp-manuali'),
        api.get('/haccp-manuali/_meta/tipologie'),
        api.get('/aziende').catch(() => ({ data: [] })),
        api.get('/pdv').catch(() => ({ data: [] })),
      ]);
      setItems(mRes.data || []);
      setMeta(mtRes.data || { tipologie: {}, sezioni_standard: [] });
      setAziende(aRes.data || []);
      setPdvList(pRes.data || []);
    } catch (e) {
      toast.error('Errore nel caricamento manuali');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submitCreate = async () => {
    if (!form.titolo.trim()) { toast.error('Indica un titolo'); return; }
    setCreating(true);
    try {
      const fd = new FormData();
      fd.append('titolo', form.titolo.trim());
      fd.append('tipologia', form.tipologia);
      fd.append('use_ai', String(form.use_ai));
      if (form.azienda_id) fd.append('azienda_id', form.azienda_id);
      if (form.ul_id) fd.append('ul_id', form.ul_id);
      const r = await api.post('/haccp-manuali', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 240000, // AI generation puó richiedere 1-2 min
      });
      toast.success(form.use_ai ? 'Manuale creato! L\'AI ha generato 10 sezioni iniziali.' : 'Manuale creato (vuoto).');
      setOpenCreate(false);
      setForm({ titolo: '', tipologia: 'ristorante', azienda_id: '', ul_id: '', use_ai: true });
      setCurrent(r.data);
      setActiveSection(0);
      setView('editor');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore creazione');
    } finally {
      setCreating(false);
    }
  };

  const openEditor = async (id) => {
    try {
      const r = await api.get(`/haccp-manuali/${id}`);
      setCurrent(r.data);
      setActiveSection(0);
      setView('editor');
    } catch (e) {
      toast.error('Manuale non trovato');
    }
  };

  const saveSection = async () => {
    if (!current) return;
    const sec = current.sezioni[activeSection];
    try {
      await api.put(`/haccp-manuali/${current.id}/section`, {
        section_id: sec.id, contenuto: sec.contenuto,
      });
      toast.success('Sezione salvata');
    } catch (e) {
      toast.error('Errore salvataggio');
    }
  };

  const regenerateSection = async () => {
    if (!current) return;
    const sec = current.sezioni[activeSection];
    setRegenerating(sec.id);
    try {
      const fd = new FormData();
      fd.append('section_id', sec.id);
      const r = await api.post(`/haccp-manuali/${current.id}/regenerate-section`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000,
      });
      const updated = { ...current };
      updated.sezioni = [...current.sezioni];
      updated.sezioni[activeSection] = {
        ...updated.sezioni[activeSection],
        contenuto: r.data.contenuto, ai_generated: true,
      };
      setCurrent(updated);
      toast.success('Sezione rigenerata con AI');
    } catch (e) {
      toast.error('Errore rigenerazione');
    } finally {
      setRegenerating(null);
    }
  };

  const finalize = async () => {
    if (!current) return;
    if (!window.confirm('Finalizzare il manuale? Dopo non potrai più modificarlo (potrai crearne una nuova revisione).')) return;
    try {
      const fd = new FormData();
      fd.append('status', 'finalized');
      await api.put(`/haccp-manuali/${current.id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Manuale finalizzato');
      setCurrent({ ...current, status: 'finalized' });
      load();
    } catch (e) {
      toast.error('Errore finalizzazione');
    }
  };

  const exportFile = async (kind) => {
    if (!current) return;
    try {
      const r = await api.get(`/haccp-manuali/${current.id}/export-${kind}`, { responseType: 'blob' });
      const blob = new Blob([r.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${current.titolo || 'manuale'}_HACCP.${kind === 'pdf' ? 'pdf' : 'docx'}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Errore export');
    }
  };

  const deleteManuale = async (m) => {
    if (!window.confirm(`Eliminare il manuale "${m.titolo}"?`)) return;
    try {
      await api.delete(`/haccp-manuali/${m.id}`);
      toast.success('Eliminato');
      load();
    } catch (e) {
      toast.error('Errore eliminazione');
    }
  };

  // ---- EDITOR VIEW ----
  if (view === 'editor' && current) {
    const sec = current.sezioni[activeSection] || {};
    const isFinalized = current.status === 'finalized';
    return (
      <div className="max-w-7xl mx-auto px-4 py-6" data-testid="manuale-editor">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => { setView('list'); setCurrent(null); }}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Indietro
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-emerald-600" /> {current.titolo}
              </h1>
              <p className="text-xs text-slate-500">
                {current.azienda_nome || '—'} · {current.tipologia_label} · Rev. {current.revisione}
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => exportFile('pdf')} data-testid="export-pdf-btn">
              <FileText className="w-4 h-4 mr-1" /> PDF
            </Button>
            <Button size="sm" variant="outline" onClick={() => exportFile('docx')} data-testid="export-docx-btn">
              <FileDown className="w-4 h-4 mr-1" /> DOCX
            </Button>
            {!isFinalized && (
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={finalize} data-testid="finalize-btn">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Finalizza
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
          {/* Sidebar sezioni */}
          <div className="space-y-1">
            {current.sezioni.map((s, i) => (
              <button
                key={s.id}
                onClick={() => setActiveSection(i)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition border ${
                  i === activeSection
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-900 font-medium'
                    : 'bg-white border-slate-200 hover:bg-slate-50 text-slate-700'
                }`}
                data-testid={`section-tab-${s.id}`}
              >
                {s.titolo}
                {s.ai_generated && <Sparkles className="inline w-3 h-3 ml-1 text-violet-500" />}
              </button>
            ))}
          </div>

          {/* Editor sezione */}
          <Card className="border-slate-200">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h2 className="text-lg font-semibold text-slate-900">{sec.titolo}</h2>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={regenerateSection} disabled={isFinalized || regenerating === sec.id} data-testid="regenerate-section-btn">
                    {regenerating === sec.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                    Rigenera con AI
                  </Button>
                  <Button size="sm" onClick={saveSection} disabled={isFinalized} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-section-btn">
                    <Save className="w-4 h-4 mr-1" /> Salva
                  </Button>
                </div>
              </div>
              <Textarea
                rows={22}
                value={sec.contenuto || ''}
                onChange={(e) => {
                  const updated = { ...current };
                  updated.sezioni = [...current.sezioni];
                  updated.sezioni[activeSection] = { ...sec, contenuto: e.target.value };
                  setCurrent(updated);
                }}
                disabled={isFinalized}
                className="font-mono text-xs leading-relaxed"
                placeholder="Scrivi qui il contenuto della sezione (puoi usare Markdown: **grassetto**, ## sottotitoli, - liste)"
                data-testid="section-content-textarea"
              />
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>{(sec.contenuto || '').length} caratteri</span>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" disabled={activeSection === 0} onClick={() => setActiveSection(activeSection - 1)}>
                    <ChevronLeft className="w-3 h-3 mr-1" /> Precedente
                  </Button>
                  <Button size="sm" variant="ghost" disabled={activeSection === current.sezioni.length - 1} onClick={() => setActiveSection(activeSection + 1)}>
                    Successiva <ChevronRight className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ---- LIST VIEW ----
  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="haccp-manuali-page">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow">
            <BookOpen className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Crea Manuale HACCP</h1>
            <p className="text-sm text-slate-500">Genera manuali HACCP completi con AI (10 sezioni) → modifica → esporta PDF/DOCX</p>
          </div>
        </div>
        <Button onClick={() => setOpenCreate(true)} className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700" data-testid="new-manuale-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuovo manuale
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Caricamento...
        </div>
      ) : items.length === 0 ? (
        <Card className="border-dashed border-2 border-slate-200">
          <CardContent className="text-center py-12">
            <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Nessun manuale ancora creato.</p>
            <p className="text-xs text-slate-400 mt-1">Clicca &quot;Nuovo manuale&quot; per iniziare. L&apos;AI genera 10 sezioni in 1-2 minuti.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="manuali-list">
          {items.map((m) => {
            const cfg = STATUS_BADGE[m.status] || STATUS_BADGE.draft;
            return (
              <Card key={m.id} className="hover:shadow-md transition cursor-pointer border-slate-200" data-testid={`manuale-row-${m.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div className="flex-1 min-w-[280px]" onClick={() => openEditor(m.id)}>
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <BookOpen className="w-4 h-4 text-emerald-600" />
                        <span className="font-semibold text-slate-900">{m.titolo}</span>
                        <Badge className={`text-xs ${cfg.cls} border`}>{cfg.label}</Badge>
                        <Badge variant="outline" className="text-xs">Rev. {m.revisione}</Badge>
                      </div>
                      <div className="text-xs text-slate-600">
                        {m.azienda_nome && <span><b>Azienda:</b> {m.azienda_nome} · </span>}
                        {m.ul_nome && <span><b>UL:</b> {m.ul_nome} · </span>}
                        <span><b>Tipo:</b> {m.tipologia_label}</span>
                        <br />
                        <span className="text-slate-400">Aggiornato: {m.updated_at?.slice(0, 16).replace('T', ' ')}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => openEditor(m.id)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`open-manuale-${m.id}`}>
                        Apri
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteManuale(m)} data-testid={`delete-manuale-${m.id}`}>
                        <Trash2 className="w-3 h-3 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuovo Manuale HACCP</DialogTitle>
            <DialogDescription>
              L&apos;AI (GPT-4o) genererà 10 sezioni complete del manuale personalizzate per la tua attività. Tempo: ~60-90 secondi.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Titolo manuale *</Label>
              <Input
                value={form.titolo}
                onChange={(e) => setForm({ ...form, titolo: e.target.value })}
                placeholder="es. Manuale HACCP Pizzeria da Mario — 2026"
                data-testid="create-titolo"
              />
            </div>
            <div>
              <Label>Tipologia attività *</Label>
              <select
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                value={form.tipologia}
                onChange={(e) => setForm({ ...form, tipologia: e.target.value })}
                data-testid="create-tipologia"
              >
                {Object.entries(meta.tipologie || {}).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Azienda</Label>
              <select
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                value={form.azienda_id}
                onChange={(e) => setForm({ ...form, azienda_id: e.target.value, ul_id: '' })}
                data-testid="create-azienda"
              >
                <option value="">— Nessuna —</option>
                {aziende.map((a) => (
                  <option key={a.id || a._id} value={a.id || a._id}>
                    {a.denominazione || a.ragione_sociale || a.nome || '—'}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <Label>Unità Locale</Label>
              <select
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                value={form.ul_id}
                onChange={(e) => setForm({ ...form, ul_id: e.target.value })}
                data-testid="create-ul"
              >
                <option value="">— Nessuna —</option>
                {pdvList
                  .filter((p) => !form.azienda_id || p.azienda_id === form.azienda_id)
                  .map((p) => (
                    <option key={p.id || p._id} value={p.id || p._id}>
                      {p.name || p.nome || p.denominazione || '—'}
                    </option>
                  ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.use_ai}
                  onChange={(e) => setForm({ ...form, use_ai: e.target.checked })}
                  data-testid="create-use-ai"
                />
                <Sparkles className="w-4 h-4 text-violet-500" />
                <span><b>Genera contenuti con AI</b> (consigliato — crea le 10 sezioni complete personalizzate)</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCreate(false)} disabled={creating}>
              Annulla
            </Button>
            <Button onClick={submitCreate} disabled={creating} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-submit">
              {creating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {form.use_ai ? 'Generazione AI in corso (~90s)...' : 'Creazione...'}</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Crea manuale</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
